import { useLocalStorageState, useMount } from 'ahooks';
import {
  App,
  Button,
  Descriptions,
  DescriptionsProps,
  Form,
  Select,
  Space,
  Typography,
} from 'antd';
import { Fragment, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { WebBookingForm } from '@/components/booking';
import { WebCustomerRegistrationForm } from '@/components/customer';
import { useDelayedLoading } from '@/hooks';
import { useAutoFillFormOnCtrlD } from '@/hooks/useAutoFillForm';
import { BackendError } from '@/lib/react-query';
import { Client, ErrorMessage } from '@/models';
import { CustomerRegistration, WebBooking } from '@/models/web.booking.model';
import { useWebCreateBookingReqeust } from '@/web-api/public';
import { useRegisterCustomer } from '@/web-api/public/useRegisterCustomer';

import { WebLayout } from '.';

const { Title, Paragraph, Link } = Typography;

export function WebRequestForm() {
  const [isNewCustomer, setNewCustomer] = useState<boolean>();
  const [bookingForm] = Form.useForm<WebBooking & { client?: Client; rememberMe?: boolean }>();
  const [customerForm] = Form.useForm<CustomerRegistration>();
  const { t } = useTranslation();
  const { modal } = App.useApp();
  const [localFormData, setLocalFormData] = useLocalStorageState<
    Pick<WebBooking, 'customerNumber' | 'contactPerson' | 'onsite'> | undefined
  >('webBookingForm');

  useMount(() => {
    if (localFormData) {
      const items: DescriptionsProps['items'] = [
        {
          key: 'customerNumber',
          label: t('forms:customerNumber'),
          children: localFormData.customerNumber,
        },
        {
          key: 'contactInfoName',
          label: t('forms:name'),
          children: localFormData.contactPerson?.name,
        },
        {
          key: 'contactInfoEmail',
          label: t('forms:email'),
          children: localFormData.contactPerson?.email,
        },
        {
          key: 'contactInfoPhone',
          label: t('forms:phone'),
          children: localFormData.contactPerson?.phone,
        },
        ...(localFormData.onsite
          ? [
              {
                key: 'onsiteLocation',
                label: t('forms:location'),
                children: (
                  <Space orientation='vertical'>
                    {localFormData.onsite?.jobSite?.name}
                    {localFormData.onsite?.jobSite?.street}
                    {localFormData.onsite?.jobSite?.supplement}
                    <span>
                      {localFormData.onsite?.jobSite?.zip}&nbsp;
                      {localFormData.onsite?.jobSite?.city}
                    </span>
                  </Space>
                ),
              },
            ]
          : []),
      ];

      void modal.confirm({
        centered: true,
        title: t('common:bookingProcessingFormAutoFillTitle'),
        okText: t('common:useAutoFill'),
        onOk: () => bookingForm.setFieldsValue(localFormData),
        content: (
          <Fragment>
            <Paragraph>{t('common:bookingProcessingFormAutoFillDescription')}</Paragraph>
            <Descriptions column={1} bordered size='small' items={items} />
          </Fragment>
        ),
        width: '60%',
      });
    }
  });

  useAutoFillFormOnCtrlD(isNewCustomer ? customerForm : bookingForm, isNewCustomer === true);

  const createBookingRequestMutation = useWebCreateBookingReqeust();
  const registerCustomerMutation = useRegisterCustomer();

  const showDelayedLoading = useDelayedLoading(createBookingRequestMutation.isPending);

  function handleCustomerStatus(value: 'true' | 'false') {
    setNewCustomer(value === 'true');
  }

  function onFinishCustomerRegistration(values: CustomerRegistration) {
    registerCustomerMutation.mutate(values, {
      onSuccess: () => {
        customerForm.resetFields();
        void modal.success({
          title: t('forms:newCustomerSuccessTitle'),
          content: t('forms:newCustomerSuccessContent'),
        });
      },
    });
  }

  function onFinish(values: WebBooking & { client?: Client; rememberMe?: boolean }) {
    createBookingRequestMutation.mutate(values, {
      onSuccess: () => {
        bookingForm.resetFields();
        if (values.rememberMe) {
          setLocalFormData({
            customerNumber: values.customerNumber,
            contactPerson: values.contactPerson,
            onsite: values.onsite,
          });
        } else {
          setLocalFormData(undefined);
        }
        void modal.success({
          title: t('forms:bookingProcessingFormSuccessTitle'),
          content: t('forms:bookingProcessingFormSuccessContent'),
        });
      },
      onError: (error: unknown) => {
        const backendError = error as BackendError;
        if (backendError.code === ErrorMessage.LOCATION_OUT_OF_RANGE) {
          void modal.warning({
            title: t(`common:locationWarningTitle`),
            content: t(`common:locationWarningDescription`),
          });
        }
      },
    });
  }

  return (
    <WebLayout isLoading={showDelayedLoading}>
      <Fragment>
        <Title level={2}>{t('forms:bookingProcessingFormTitle')}</Title>
        <Paragraph>
          {t('forms:bookingProcessingFormDescription')}:
          <Link href='https://smp-berlin.de/konditionen' target='_blank' rel='noreferrer'>
            &nbsp;{t('common:termsOfUse')}
          </Link>
        </Paragraph>
        <Paragraph>{t('forms:bookingProcessingFormIntro')}:</Paragraph>
        <Select
          title='customer'
          value={isNewCustomer === true ? 'true' : 'false'}
          onChange={handleCustomerStatus}
          style={{ width: '100%', marginBottom: 24 }}
          aria-label='Customer status'
        >
          <Select.Option value='true'>{t('forms:newCustomer')}</Select.Option>
          <Select.Option value='false'>{t('forms:existingCustomer')}</Select.Option>
        </Select>
        {isNewCustomer ? (
          <WebCustomerRegistrationForm
            form={customerForm}
            onFinish={onFinishCustomerRegistration}
          />
        ) : (
          <WebBookingForm form={bookingForm} onFinish={onFinish} />
        )}
        <Button
          type='primary'
          onClick={isNewCustomer ? customerForm.submit : bookingForm.submit}
          disabled={
            isNewCustomer
              ? registerCustomerMutation.isPending
              : createBookingRequestMutation.isPending
          }
        >
          {t('forms:sendRequest')}
        </Button>
      </Fragment>
    </WebLayout>
  );
}
